import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, CheckCircle, AlertTriangle, XCircle, AlertCircle, MapPin, Loader2 } from 'lucide-react';
import { AssetCondition, ExceptionType } from '@/types/asset-audit';
import { dataService } from '@/services/data-service';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { cn } from '@/lib/utils';
import { AssetSearchWithSuggestions } from './AssetSearchWithSuggestions';

interface CreateManualExceptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ManualExceptionData) => void;
}

export interface ManualExceptionData {
  assetCode: string;
  assetName?: string;
  exceptionType: string;
  severity: string;
  priority: string;
  description: string;
  location?: string;
  subLocation?: string;
  condition?: AssetCondition;
}

interface Asset {
  assetId: string;
  code?: string;
  name: string;
  location?: string;
  subLocation?: string;
}

interface Location {
  name: string;
  subLocations?: string[];
}

export function CreateManualExceptionModal({
  open,
  onOpenChange,
  onSubmit,
}: Readonly<CreateManualExceptionModalProps>) {
  useFeatureSettings();
  const [formData, setFormData] = useState<ManualExceptionData>({
    assetCode: '',
    assetName: '',
    exceptionType: '',
    severity: 'medium',
    priority: 'normal',
    description: '',
    location: '',
    subLocation: '',
    condition: undefined,
  });

  const [, setAssets] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [subLocations, setSubLocations] = useState<string[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Condition options with visual styling
  const conditionOptions: Array<{
    value: AssetCondition;
    label: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
    selectedBgColor: string;
  }> = [
    {
      value: 'Good' as AssetCondition,
      label: 'Good Condition',
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
      selectedBgColor: 'bg-green-100',
    },
    {
      value: 'Fair' as AssetCondition,
      label: 'Fair Condition',
      icon: <AlertCircle className="w-5 h-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-500',
      selectedBgColor: 'bg-blue-100',
    },
    {
      value: 'Poor' as AssetCondition,
      label: 'Poor Condition',
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-500',
      selectedBgColor: 'bg-yellow-100',
    },
    {
      value: 'Damaged' as AssetCondition,
      label: 'Damaged',
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-500',
      selectedBgColor: 'bg-orange-100',
    },
    {
      value: 'Critical' as AssetCondition,
      label: 'Critical',
      icon: <XCircle className="w-5 h-5" />,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      selectedBgColor: 'bg-red-100',
    },
  ];

  // Load assets when modal opens
  useEffect(() => {
    if (open) {
      loadAssets();
      loadLocations();
    }
  }, [open]);

  // Update sub-locations when location changes
  useEffect(() => {
    if (formData.location) {
      const location = locations.find(loc => loc.name === formData.location);
      setSubLocations(location?.subLocations ?? []);
      // Reset sub-location if it's not in the new list
      if (location?.subLocations && !location.subLocations.includes(formData.subLocation ?? '')) {
        setFormData(prev => ({ ...prev, subLocation: '' }));
      }
    } else {
      setSubLocations([]);
      setFormData(prev => ({ ...prev, subLocation: '' }));
    }
  }, [formData.location, locations]);

  const loadAssets = async () => {
    setIsLoadingAssets(true);
    try {
      // Fetch assets based on feature settings (API or Demo)
      const response = await dataService.getAssets({ pageSize: 1000 });
      const assetData = response.data.map((asset: any) => ({
        assetId: asset.assetId,
        code: asset.code || asset.assetId,
        name: asset.name,
        location: asset.location,
        subLocation: asset.subLocation,
      }));
      setAssets(assetData);
    } catch (error) {
      console.error('Failed to load assets:', error);
      // Fallback to empty array
      setAssets([]);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const loadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      // Fetch unique locations from assets
      const response = await dataService.getAssets({ pageSize: 1000 });
      const assetsData = response.data;
      
      // Extract unique locations and their sub-locations
      const locationMap = new Map<string, Set<string>>();
      
      assetsData.forEach((asset: any) => {
        if (asset.location) {
          if (!locationMap.has(asset.location)) {
            locationMap.set(asset.location, new Set());
          }
          if (asset.subLocation) {
            locationMap.get(asset.location)?.add(asset.subLocation);
          }
        }
      });
      
      // Convert to Location array
      const locationsData: Location[] = Array.from(locationMap.entries()).map(([name, subLocs]) => ({
        name,
        subLocations: Array.from(subLocs).sort((a, b) => a.localeCompare(b)),
      })).sort((a, b) => a.name.localeCompare(b.name));
      
      setLocations(locationsData);
    } catch (error) {
      console.error('Failed to load locations:', error);
      setLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onOpenChange(false);
    // Reset form
    setFormData({
      assetCode: '',
      assetName: '',
      exceptionType: '',
      severity: 'medium',
      priority: 'normal',
      description: '',
      location: '',
      subLocation: '',
      condition: undefined,
    });
  };

  const handleAssetSelect = (asset: { id: string; code: string; name: string; location?: string; category?: string }) => {
    setFormData({
      ...formData,
      assetCode: asset.code,
      assetName: asset.name,
      location: asset.location || formData.location,
    });
  };

  const handleConditionSelect = (condition: AssetCondition) => {
    setFormData({ ...formData, condition });
  };

  const isConditionRequired = formData.exceptionType === ExceptionType.ConditionIssue;
  const isLocationRequired = formData.exceptionType === ExceptionType.LocationMismatch;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Manual Exception
          </DialogTitle>
          <DialogDescription>
            Manually create an audit exception for tracking and resolution.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Asset Code Search */}
          <div className="space-y-2">
            <Label htmlFor="assetCode">Asset Code *</Label>
            <AssetSearchWithSuggestions
              onAssetSelect={handleAssetSelect}
              placeholder="Search for an asset by code, name, or location..."
              className="w-full"
            />
            {formData.assetCode && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border">
                <div className="flex-1">
                  <p className="text-sm font-medium">{formData.assetCode}</p>
                  {formData.assetName && (
                    <p className="text-xs text-muted-foreground">{formData.assetName}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, assetCode: '', assetName: '' })}
                >
                  Change
                </Button>
              </div>
            )}
          </div>

          {/* Exception Type */}
          <div className="space-y-2">
            <Label htmlFor="exceptionType">Exception Type *</Label>
            <Select
              value={formData.exceptionType}
              onValueChange={(value: string) => setFormData({ ...formData, exceptionType: value })}
              required
            >
              <SelectTrigger id="exceptionType" className="bg-background">
                <SelectValue placeholder="Select exception type" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value={ExceptionType.LocationMismatch}>Location Mismatch</SelectItem>
                <SelectItem value={ExceptionType.ConditionIssue}>Condition Issue</SelectItem>
                <SelectItem value={ExceptionType.NotFound}>Not Found</SelectItem>
                <SelectItem value={ExceptionType.DataDiscrepancy}>Data Discrepancy</SelectItem>
                <SelectItem value={ExceptionType.MaintenanceRequired}>Maintenance Required</SelectItem>
                <SelectItem value={ExceptionType.ComplianceIssue}>Compliance Issue</SelectItem>
                <SelectItem value={ExceptionType.UnauthorizedMovement}>Unauthorised Movement</SelectItem>
                <SelectItem value={ExceptionType.DuplicateAsset}>Duplicate Asset</SelectItem>
                <SelectItem value={ExceptionType.Other}>Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Condition Picker - Show when Condition Issue is selected */}
          {isConditionRequired && (
            <div className="space-y-3">
              <Label>Asset Condition *</Label>
              {!formData.condition && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Required:</span> Please select the asset condition
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {conditionOptions.map((option) => {
                  const isSelected = formData.condition === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleConditionSelect(option.value)}
                      className={cn(
                        'p-4 rounded-lg border-2 transition-all',
                        isSelected 
                          ? `${option.borderColor} ${option.selectedBgColor}` 
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <div className={cn(
                        'flex flex-col items-center gap-2',
                        isSelected ? option.color : 'text-gray-600'
                      )}>
                        {option.icon}
                        <span className="text-sm font-medium text-center">
                          {option.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Location Selector - Show when Location Mismatch is selected */}
          {isLocationRequired && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MapPin className="h-4 w-4" />
                <span>Location Information</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Select
                    value={formData.location}
                    onValueChange={(value: string) => setFormData({ ...formData, location: value })}
                    required={isLocationRequired}
                    disabled={isLoadingLocations}
                  >
                    <SelectTrigger id="location" className="bg-background">
                      <SelectValue placeholder={isLoadingLocations ? "Loading..." : "Select location"} />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      {locations.map((location) => (
                        <SelectItem key={location.name} value={location.name}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subLocation">Sub-Location</Label>
                  <Select
                    value={formData.subLocation}
                    onValueChange={(value: string) => setFormData({ ...formData, subLocation: value })}
                    disabled={subLocations.length === 0}
                  >
                    <SelectTrigger id="subLocation" className="bg-background">
                      <SelectValue placeholder={
                        subLocations.length === 0 
                          ? 'No sub-locations available' 
                          : 'Select sub-location (optional)'
                      } />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      {subLocations.map((subLocation) => (
                        <SelectItem key={subLocation} value={subLocation}>
                          {subLocation}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Severity and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="severity">Severity *</Label>
              <Select
                value={formData.severity}
                onValueChange={(value: string) => setFormData({ ...formData, severity: value })}
              >
                <SelectTrigger id="severity" className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: string) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger id="priority" className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Provide detailed information about the exception."
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!formData.assetCode || isLoadingAssets || isLoadingLocations}
            >
              {(isLoadingAssets || isLoadingLocations) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Exception
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
